import React from "react";
import classNames from "classnames";
import moment from "moment";
import "./StageTracker.css";

// stages: [{ key, label, status: "pending" | "active" | "done", link, onClick, selected, days, date }]

const STAGE_ICONS = {
  lead: "bx bx-user",
  rfp: "bx bx-file",
  proposal: "bx bx-file-find",
  contract: "bx bx-edit-alt",
  customer: "bx bxs-badge-check",
};

const StageChip = ({ stage }) => {
  const classes = classNames("stage-chip", {
    active: stage.status === "active",
    complete: stage.status === "done",
    lost: stage.status === "lost",
  });

  const content = (
    <span className={classes}>
      <span className="stage-chip-dot">
        {stage.status === "done" ? (
          <i className="fas fa-check"></i>
        ) : stage.status === "lost" ? (
          <i className="fas fa-times"></i>
        ) : (
          <i className={STAGE_ICONS[stage.key]}></i>
        )}
      </span>
      <span className="stage-chip-label">{stage.label}</span>
      {stage.status === "active" && Number.isFinite(stage.days) && (
        <span className="stage-chip-days">{stage.days}d</span>
      )}
    </span>
  );

  return stage.link ? (
    <a href={stage.link} target="_blank" rel="noreferrer" className="stage-chip-link">
      {content}
    </a>
  ) : (
    content
  );
};

// Single-line horizontal tracker — used inline (table row, compact spaces)
const InlineStageTracker = ({ stages }) => (
  <div className="stage-tracker-inline">
    {stages.map((stage, i) => (
      <React.Fragment key={stage.key}>
        {i > 0 && <span className="stage-tracker-arrow" />}
        <StageChip stage={stage} />
      </React.Fragment>
    ))}
  </div>
);

// Full stepper with icon circles — used in the View Lead modal header
const FullStageTracker = ({ stages }) => (
  <ol className="stage-tracker mb-0">
    {stages.map((stage) => {
      const classes = classNames("stage-tracker-step", {
        active: stage.status === "active",
        complete: stage.status === "done",
        lost: stage.status === "lost",
        "inline-selected": stage.selected,
        clickable: Boolean(stage.link || stage.onClick),
      });

      const content = (
        <>
          <div className="stage-tracker-circle">
            {stage.status === "done" ? (
              <i className="fas fa-check"></i>
            ) : stage.status === "lost" ? (
              <i className="fas fa-times"></i>
            ) : (
              <i className={STAGE_ICONS[stage.key]}></i>
            )}
          </div>
          <div className="stage-tracker-caption">
            <span>{stage.label}</span>
            {stage.status === "active" && Number.isFinite(stage.days) && (
              <div className="stage-tracker-days">
                {stage.days} day{stage.days === 1 ? "" : "s"}
              </div>
            )}
            {(stage.status === "done" || stage.status === "active" || stage.status === "lost") && stage.date && (
              <div className="stage-tracker-date">
                {stage.status === "done" ? "Completed " : stage.status === "lost" ? "Lost " : "Since "}
                {moment(stage.date).format("DD MMM YYYY")}
              </div>
            )}
          </div>
        </>
      );

      return (
        <li className={classes} key={stage.key}>
          {stage.link ? (
            <a href={stage.link} target="_blank" rel="noreferrer">
              {content}
            </a>
          ) : stage.onClick ? (
            <a onClick={stage.onClick} style={{ cursor: "pointer" }}>
              {content}
            </a>
          ) : (
            content
          )}
        </li>
      );
    })}
  </ol>
);

// Three-line compact tracker for the Lead table's "Current Stage" column:
// current stage name on top, a dot line (RFP · Proposal · Contract ·
// Customer — "Lead" is the trivial starting point so it isn't given a dot),
// then every dot's own label underneath it in as small a font as still
// reads, so the whole thing spans the column width edge to edge.
const DotsStageTracker = ({ stages }) => {
  const dotStages = stages.filter((stage) => stage.key !== "lead");
  const activeStage = stages.find((stage) => stage.status === "active");

  return (
    <div className="stage-dots-wrap">
      {activeStage && (
        <div className="stage-dots-current">
          {activeStage.label}
          {Number.isFinite(activeStage.days) && ` · ${activeStage.days}d`}
        </div>
      )}
      <div className="stage-dots-grid">
        {dotStages.map((stage) => {
          const classes = {
            active: stage.status === "active",
            complete: stage.status === "done",
            lost: stage.status === "lost",
          };
          return (
            <div key={stage.key} className={classNames("stage-dot-col", classes)}>
              <span
                className={classNames("stage-dot", classes)}
                title={
                  stage.status === "active" && Number.isFinite(stage.days)
                    ? `${stage.label} · ${stage.days}d`
                    : stage.label
                }
              />
              <span className={classNames("stage-dot-name", classes)} title={stage.label}>
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StageTracker = ({ stages, compact, dots }) => {
  if (dots) return <DotsStageTracker stages={stages} />;
  return compact ? <InlineStageTracker stages={stages} /> : <FullStageTracker stages={stages} />;
};

export default StageTracker;
